/**
 * scene-beat-decomposition (Phase 5+ Tier 5G 第 1 段) Phase 2.1 UI client:
 * client-player から Beat API を呼び出すための軽量 fetch wrapper。
 *
 * 設計 doctrine:
 * - client-player は core-domain への dep を持たない (= memory
 *   `project_client_player_no_core_domain_dep`)、 types は本 file 内で独自 declaration
 * - sibling: scene-summary-editorial.ts と同 pattern (= manual registration endpoint、
 *   openapi-fetch 外)
 *
 * 設計対応:
 *   - design.md §Architecture
 *   - requirements.md Phase 2.1
 */
import { ApiError, type ProblemDetail } from "./client";

// ─── 独自 type declaration (= core-domain dep なし) ─────────

export type BeatFunctionType =
  | "setup"
  | "inciting"
  | "rising"
  | "climax"
  | "falling"
  | "denouement";

export interface BeatResponse {
  id: string;
  scenarioId: string;
  order: number;
  functionType: BeatFunctionType;
  customLabel: string | null;
  triggerCondition: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface BeatListResponse {
  beats: BeatResponse[];
}

export interface BeatTimelineEntryResponse {
  beat: BeatResponse;
  sceneIds: string[];
}

export interface BeatTimelineResponse {
  timeline: BeatTimelineEntryResponse[];
}

// ─── client functions ─────────

function fallbackProblem(status: number, statusText: string): ProblemDetail {
  return {
    type: "about:blank",
    title: "Request failed",
    status,
    detail: statusText !== "" ? statusText : "Unknown error",
  };
}

async function parseProblem(
  response: Response,
): Promise<ProblemDetail> {
  try {
    const json = (await response.json()) as ProblemDetail;
    if (typeof json.type === "string" && typeof json.title === "string") {
      return json;
    }
  } catch {
    /* JSON parse 失敗時は fallback */
  }
  return fallbackProblem(response.status, response.statusText);
}

export async function listBeats(
  baseUrl: string,
  scenarioId: string,
): Promise<BeatListResponse> {
  const response = await fetch(
    `${baseUrl}/scenarios/${encodeURIComponent(scenarioId)}/beats`,
    { method: "GET", headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }
  return (await response.json()) as BeatListResponse;
}

export async function listBeatTimeline(
  baseUrl: string,
  scenarioId: string,
): Promise<BeatTimelineResponse> {
  const response = await fetch(
    `${baseUrl}/scenarios/${encodeURIComponent(scenarioId)}/beats/timeline`,
    { method: "GET", headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }
  return (await response.json()) as BeatTimelineResponse;
}

export interface CreateBeatInput {
  order: number;
  functionType: BeatFunctionType;
  customLabel?: string | null;
  triggerCondition?: unknown;
}

export async function createBeat(
  baseUrl: string,
  scenarioId: string,
  input: CreateBeatInput,
): Promise<BeatResponse> {
  const response = await fetch(
    `${baseUrl}/author/scenarios/${encodeURIComponent(scenarioId)}/beats`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }
  return (await response.json()) as BeatResponse;
}

/**
 * scene-beat-decomposition (Phase 5+ Tier 5G 第 1 段) 2026-05-20 preview resolution:
 * triggerCondition の dry-run wrapper。 sceneIndex / lastChoiceId を context として渡し、
 * 「現在の context で何 Beat がマッチするか」 を確認する。 creator UI の dry-run button で
 * 利用想定。
 */
export interface PreviewBeatResolutionInput {
  sceneIndex?: number;
  lastChoiceId?: string;
}

export type PreviewBeatResolutionResponse =
  | { matched: false }
  | { matched: true; beat: BeatResponse };

export async function previewBeatResolution(
  baseUrl: string,
  scenarioId: string,
  input: PreviewBeatResolutionInput,
): Promise<PreviewBeatResolutionResponse> {
  const params = new URLSearchParams();
  if (input.sceneIndex !== undefined) {
    params.set("sceneIndex", String(input.sceneIndex));
  }
  if (input.lastChoiceId !== undefined) {
    params.set("lastChoiceId", input.lastChoiceId);
  }
  const qs = params.toString();
  const url = `${baseUrl}/scenarios/${encodeURIComponent(scenarioId)}/beats/preview-resolution${qs !== "" ? `?${qs}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }
  return (await response.json()) as PreviewBeatResolutionResponse;
}

/**
 * scene-beat-decomposition (Phase 5+ Tier 5G 第 1 段) post-closure 拡張:
 * Beat の partial update wrapper。 dry-run preview で発見した不整合の修正経路。
 * 全 field optional、 undefined / key 省略で変更なし、 customLabel / triggerCondition は null
 * 明示で消去。 order / scenarioId は本 input では変更不可。
 */
export interface UpdateBeatInput {
  functionType?: BeatFunctionType;
  customLabel?: string | null;
  triggerCondition?: unknown;
}

export async function updateBeat(
  baseUrl: string,
  scenarioId: string,
  beatId: string,
  input: UpdateBeatInput,
): Promise<BeatResponse> {
  const response = await fetch(
    `${baseUrl}/author/scenarios/${encodeURIComponent(scenarioId)}/beats/${encodeURIComponent(beatId)}`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }
  return (await response.json()) as BeatResponse;
}

export async function deleteBeat(
  baseUrl: string,
  scenarioId: string,
  beatId: string,
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/author/scenarios/${encodeURIComponent(scenarioId)}/beats/${encodeURIComponent(beatId)}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }
}
